import type { ReactNode } from 'react';

/** How a link is shown; the chat adds a button behind links that can become a recipe. */
export type RenderLink = (href: string, label: string, key: number) => ReactNode;

/**
 * The little Markdown the chat needs, rendered as React elements (no HTML string, nothing to
 * sanitize): paragraphs, headings, bullet and numbered lists, **bold**, *italic*, `code` and
 * links. Links open in a new tab; anything that is not http(s) stays plain text.
 */
export function MarkdownLite({ text, renderLink }: { text: string; renderLink?: RenderLink }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i++;
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push(
        <p key={key++} className="mt-2 font-semibold">
          {inline(heading[2] ?? '', renderLink)}
        </p>,
      );
      i++;
      continue;
    }
    if (/^\s*```/.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i] ?? '')) {
        code.push(lines[i] ?? '');
        i++;
      }
      i++;
      blocks.push(
        <pre key={key++} className="overflow-x-auto rounded bg-muted p-2 text-xs">
          {code.join('\n')}
        </pre>,
      );
      continue;
    }
    const bullet = /^\s*[-*+]\s+/;
    const numbered = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || numbered.test(line)) {
      const ordered = numbered.test(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? numbered : bullet).test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(ordered ? numbered : bullet, ''));
        i++;
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List
          key={key++}
          className={ordered ? 'list-decimal space-y-0.5 pl-5' : 'list-disc space-y-0.5 pl-5'}
        >
          {items.map((item, n) => (
            <li key={n}>{inline(item, renderLink)}</li>
          ))}
        </List>,
      );
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() &&
      !bullet.test(lines[i] ?? '') &&
      !numbered.test(lines[i] ?? '') &&
      !/^(#{1,6})\s+/.test(lines[i] ?? '') &&
      !/^\s*```/.test(lines[i] ?? '')
    ) {
      paragraph.push(lines[i] ?? '');
      i++;
    }
    blocks.push(<p key={key++}>{inline(paragraph.join(' '), renderLink)}</p>);
  }
  return <div className="space-y-2 text-sm leading-relaxed">{blocks}</div>;
}

const INLINE =
  /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>)]+)/g;

function inline(text: string, renderLink?: RenderLink): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const token = m[0];
    if (token.startsWith('**')) out.push(<strong key={n++}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith('`'))
      out.push(
        <code key={n++} className="rounded bg-muted px-1 text-xs">
          {token.slice(1, -1)}
        </code>,
      );
    else if (token.startsWith('*')) out.push(<em key={n++}>{token.slice(1, -1)}</em>);
    else if (token.startsWith('[')) {
      const label = token.slice(1, token.indexOf(']'));
      if (renderLink) out.push(renderLink(m[2]!, label, n++));
      else
        out.push(
          <a
            key={n++}
            href={m[2]}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline"
          >
            {label}
          </a>,
        );
    } else if (renderLink) out.push(renderLink(token, token, n++));
    else
      out.push(
        <a
          key={n++}
          href={token}
          target="_blank"
          rel="noreferrer noopener"
          className="break-all text-primary underline"
        >
          {token}
        </a>,
      );
    last = start + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
